import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { screenReaderOnlyStyle } from 'style/config';
import Actions from 'Actions';
import { updateBasket } from 'actions/RwdBasketActions';
import Button from 'components/Button';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import addToCart from 'services/api/basket/addToCart';
import classifyAtbResult, { ATB_RESULT } from 'components/ProductPage/PdpBundling/BundleWidget/utils/classifyAtbResult';
import buildBundleAtbProducts from 'components/ProductPage/PdpBundling/BundleWidget/utils/buildBundleAtbProducts';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import { CTA } from 'constants/constructorConstants';
import Empty from 'constants/empty';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const AddAllButton = ({
    disabled,
    selectedItems = Empty.Array,
    currentProduct,
    allBundleProducts = Empty.Array,
    deselectedSkuIds,
    bundleName,
    bundleType,
    isConstructorRecommendations
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const currentSku = currentProduct?.currentSku;
    const itemsToAdd = useMemo(() => (currentSku ? [currentSku, ...selectedItems] : selectedItems), [currentSku, selectedItems]);

    const itemCount = itemsToAdd.length;
    const allProducts = useMemo(() => (currentSku ? [currentSku, ...allBundleProducts] : allBundleProducts), [currentSku, allBundleProducts]);

    const fireAtbTracking = useCallback(
        addedSkuIds => {
            const productStrings = buildBundleAtbProducts({
                products: allProducts,
                addedSkuIds,
                deselectedSkuIds,
                bundleName,
                bundleType
            });

            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    actionInfo: anaConsts.EVENT_NAMES.PRODUCT_PAGE.PDP_BUNDLE_ADD_ALL_TO_BASKET,
                    linkName: anaConsts.EVENT_NAMES.PRODUCT_PAGE.PDP_BUNDLE_ADD_ALL_TO_BASKET,
                    eventStrings: [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_337],
                    productStrings
                }
            });
        },
        [allProducts, deselectedSkuIds, bundleName, bundleType]
    );

    const showAtbErrorModal = useCallback(() => {
        const errorMessage = getText('atbErrorMessage');
        const productStrings = buildBundleAtbProducts({
            products: allProducts,
            addedSkuIds: Empty.Array,
            deselectedSkuIds,
            bundleName,
            bundleType
        });

        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('atbErrorTitle'),
                message: errorMessage,
                buttonText: getText('atbErrorOk')
            })
        );

        processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
            data: {
                pageName: `${anaConsts.PAGE_TYPES.PDP_BUNDLE_ERROR_MODAL}:n/a:*`,
                prop28: anaConsts.CONTEXT.PDP_BUNDLING,
                prop48: errorMessage,
                eventStrings: [anaConsts.Event.EVENT_337],
                productStrings
            }
        });
    }, [dispatch, allProducts, deselectedSkuIds, bundleName, bundleType]);

    const openAtbModal = useCallback(
        (response, { hasUnavailableItems, addedSkuIds }) => {
            const addedItems = itemsToAdd.filter(item => addedSkuIds.has(item.skuId));
            const modalItems = addedItems.length ? addedItems : itemsToAdd;

            dispatch(
                updateBasket({
                    newBasket: basketUtils.calculateUpdatedBasketProductBundling(response),
                    shouldCalculateRootBasketType: false
                })
            );

            dispatch(
                Actions.showAddToBasketModal({
                    isOpen: true,
                    sku: modalItems,
                    product: currentProduct,
                    isPdpBundling: true,
                    hasUnavailableItems,
                    bundleName,
                    bundleType
                })
            );
        },
        [dispatch, itemsToAdd, currentProduct, bundleName, bundleType]
    );

    const handleClick = useCallback(async () => {
        if (isSubmitting || !itemsToAdd.length) {
            return;
        }

        setIsSubmitting(true);

        const requestedSkuIds = itemsToAdd.map(sku => sku.skuId);
        const skuList = itemsToAdd.map(sku => ({
            skuId: sku.skuId,
            productId: sku.productId || '',
            qty: sku.qty || 1
        }));

        try {
            const response = await addToCart({ orderId: basketUtils.getOrderId(), skuList });
            const outcome = classifyAtbResult(requestedSkuIds, response);

            if (outcome === ATB_RESULT.ERROR) {
                showAtbErrorModal();
            } else {
                const addedSkuIds = new Set((response?.items || []).map(item => item?.sku?.skuId).filter(Boolean));
                fireAtbTracking(addedSkuIds);
                openAtbModal(response, { hasUnavailableItems: outcome === ATB_RESULT.PARTIAL, addedSkuIds });
            }
        } catch {
            showAtbErrorModal();
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, itemsToAdd, showAtbErrorModal, openAtbModal, fireAtbTracking]);

    const isDisabled = disabled || isSubmitting;

    return (
        <React.Fragment>
            <Button
                variant='primary'
                hasMinWidth
                onClick={handleClick}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                aria-busy={isSubmitting}
                aria-label={getText('addAllToBasketAria', [itemCount])}
                loading={isSubmitting}
                {...(isConstructorRecommendations && { 'data-cnstrc-btn': CTA.ADD_TO_CART })}
            >
                {getText('addAllToBasket')}
            </Button>
            <span
                css={screenReaderOnlyStyle}
                role='status'
                aria-live='polite'
            >
                {isSubmitting ? getText('addAllToBasketLoading') : ''}
            </span>
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(AddAllButton, 'AddAllButton');
